import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  createFamily, 
  getUserFamilies, 
  joinFamily, 
  getFamilyReaders,
  refreshJoinCode,
  leaveFamily,
  deleteFamily
} from '../services/familyService';
import { createReader, deleteReader } from '../services/readerService';

function Family() {
  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [readers, setReaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form states
  const [newFamilyName, setNewFamilyName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [readerName, setReaderName] = useState('');
  const [newReaderName, setNewReaderName] = useState('');
  
  // Tip popover states
  const [showFamilyTip, setShowFamilyTip] = useState(false);
  const [showJoinTip, setShowJoinTip] = useState(false);
  const [showReaderTip, setShowReaderTip] = useState(false);
  
  // Refs for popover positioning
  const familyTipRef = useRef(null);
  const familyTipButtonRef = useRef(null);
  const joinTipRef = useRef(null);
  const joinTipButtonRef = useRef(null);
  const readerTipRef = useRef(null);
  const readerTipButtonRef = useRef(null);
  
  const navigate = useNavigate();

  // Load families on component mount
  useEffect(() => {
    loadFamilies();
  }, []);

  // Load readers when a family is selected
  useEffect(() => {
    if (selectedFamily) {
      console.log('Loading readers for family:', selectedFamily);
      loadFamilyReaders(selectedFamily);
    }
  }, [selectedFamily]);
  
  // Close tips when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle family tip
      if (showFamilyTip && 
          familyTipRef.current && 
          !familyTipRef.current.contains(event.target) &&
          familyTipButtonRef.current && 
          !familyTipButtonRef.current.contains(event.target)) {
        setShowFamilyTip(false);
      }
      
      // Handle join tip
      if (showJoinTip && 
          joinTipRef.current && 
          !joinTipRef.current.contains(event.target) &&
          joinTipButtonRef.current && 
          !joinTipButtonRef.current.contains(event.target)) {
        setShowJoinTip(false);
      }
      
      // Handle reader tip
      if (showReaderTip && 
          readerTipRef.current && 
          !readerTipRef.current.contains(event.target) &&
          readerTipButtonRef.current && 
          !readerTipButtonRef.current.contains(event.target)) {
        setShowReaderTip(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFamilyTip, showJoinTip, showReaderTip]);

  // Load all families for the current user
  const loadFamilies = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getUserFamilies();
      setFamilies(response.families || []);
      
      // Select the first family by default if available
      if (response.families && response.families.length > 0 && !selectedFamily) {
        setSelectedFamily(response.families[0].id);
      }
    } catch (err) {
      setError('Failed to load families. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load readers for a specific family
  const loadFamilyReaders = async (familyId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getFamilyReaders(familyId);
      console.log('Loaded readers:', response.readers);
      setReaders(response.readers || []);
    } catch (err) {
      setError('Failed to load family readers. Please try again.');
      console.error('Error loading readers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle creating a new family
  const handleCreateFamily = async (e) => {
    e.preventDefault();
    if (!newFamilyName.trim()) return;

    try {
      setLoading(true);
      setError(null);
      const response = await createFamily({ name: newFamilyName });
      setNewFamilyName('');
      setSuccess('Family created successfully!');
      
      // Refresh the families list
      await loadFamilies();
      
      // Select the newly created family
      if (response.family && response.family.id) {
        setSelectedFamily(response.family.id);
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to create family. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle joining a family
  const handleJoinFamily = async (e) => {
    e.preventDefault();
    if (!joinCode.trim() || !readerName.trim()) return;

    try {
      setLoading(true);
      setError(null);
      await joinFamily({ joinCode, readerName });
      setJoinCode('');
      setReaderName('');
      setSuccess('Successfully joined family!');
      
      // Refresh the families list
      await loadFamilies();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to join family. Please check your join code and try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle adding a new reader to the selected family
  const handleAddReader = async (e) => {
    e.preventDefault();
    if (!selectedFamily || !newReaderName.trim()) return;

    try {
      setLoading(true);
      setError(null);
      await createReader({ 
        familyId: selectedFamily, 
        name: newReaderName 
      });
      setNewReaderName('');
      setSuccess('Reader added successfully!');
      
      // Refresh the readers list
      await loadFamilyReaders(selectedFamily);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to add reader. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle refreshing a family's join code
  const handleRefreshJoinCode = async (familyId) => {
    try {
      setLoading(true);
      setError(null);
      await refreshJoinCode(familyId);
      setSuccess('Join code refreshed successfully!');
      
      // Refresh the families list to get the new join code
      await loadFamilies();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to refresh join code. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle leaving a family
  const handleLeaveFamily = async (familyId) => {
    if (!window.confirm('Are you sure you want to leave this family?')) return;
    
    try {
      setLoading(true);
      setError(null);
      await leaveFamily(familyId);
      setSuccess('You have left the family.');
      
      // Refresh the families list
      await loadFamilies();
      setSelectedFamily(null);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to leave family. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting a family (only for family owners)
  const handleDeleteFamily = async (familyId) => {
    if (!window.confirm('Are you sure you want to delete this family? This action cannot be undone.')) return;
    
    try {
      setLoading(true);
      setError(null);
      await deleteFamily(familyId);
      setSuccess('Family deleted successfully.');
      
      // Refresh the families list
      await loadFamilies();
      setSelectedFamily(null);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete family. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle removing a reader
  const handleRemoveReader = async (readerId) => {
    if (!window.confirm('Are you sure you want to remove this reader?')) return;
    
    try {
      setLoading(true);
      setError(null);
      await deleteReader(readerId);
      setSuccess('Reader removed successfully.');
      
      // Refresh the readers list
      await loadFamilyReaders(selectedFamily);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to remove reader. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Find the selected family object
  const currentFamily = families.find(family => family.id === selectedFamily);
  
  // Render tip popover
  const renderTipPopover = (content, ref) => {
    return (
      <div className="color-legend-popover" ref={ref}>
        <div className="color-legend-content">
          <div className="color-legend-header">
            <h5>Helpful Tips</h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => {
                if (ref === familyTipRef) setShowFamilyTip(false);
                if (ref === joinTipRef) setShowJoinTip(false);
                if (ref === readerTipRef) setShowReaderTip(false);
              }} 
              aria-label="Close"
            ></button>
          </div>
          <div className="color-legend-body">
            {content}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="family-container">
      <h1 className="family-title">Family Bible Reading</h1>
      
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      {success && (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      )}
      
      <div className="family-content">
        {/* Family Selection */}
        <div className="family-selection mb-4">
          <div className="d-flex align-items-center position-relative">
            <h2>My Families</h2>
            <button 
              className="btn btn-link p-0 ms-2" 
              onClick={() => setShowFamilyTip(!showFamilyTip)}
              aria-label="Show family tips"
              title="What is a family?"
              ref={familyTipButtonRef}
            >
              <i className="bi bi-question-circle"></i>
            </button>
            
            {/* Family Tip Popover */}
            {showFamilyTip && renderTipPopover(
              <div>
                <p>A "family" in this app can be:</p>
                <ul className="mb-0 text-start">
                  <li>Your actual family members</li>
                  <li>A group of friends reading together</li>
                  <li>Work colleagues forming a reading group</li>
                  <li>A church small group or Bible study</li>
                </ul>
                <p className="mt-2 mb-0">Create as many families as you need to organize different reading groups!</p>
              </div>,
              familyTipRef
            )}
          </div>
          
          {families.length > 0 ? (
            <div className="family-list">
              <select 
                className="form-select mb-3"
                value={selectedFamily || ''}
                onChange={(e) => setSelectedFamily(e.target.value)}
              >
                <option value="">Select a family</option>
                {families.map(family => (
                  <option key={family.id} value={family.id}>
                    {family.name}
                  </option>
                ))}
              </select>
              
              {currentFamily && (
                <div className="family-details card mb-4">
                  <div className="card-body">
                    <h3 className="card-title">{currentFamily.name}</h3>
                    <p className="card-text">
                      <strong>Join Code:</strong> {currentFamily.join_code}
                    </p>
                    <div className="family-actions">
                      <button 
                        className="btn btn-outline-primary btn-sm me-2"
                        onClick={() => handleRefreshJoinCode(currentFamily.id)}
                        disabled={loading}
                      >
                        Refresh Join Code
                      </button>
                      
                      {currentFamily.is_owner ? (
                        <button 
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleDeleteFamily(currentFamily.id)}
                          disabled={loading}
                        >
                          Delete Family
                        </button>
                      ) : (
                        <button 
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleLeaveFamily(currentFamily.id)}
                          disabled={loading}
                        >
                          Leave Family
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p>You don't have any families yet. Create or join a family below.</p>
          )}
        </div>
        
        {/* Create and Join Family Forms */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <h3 className="card-title">Create a Family</h3>
                <form onSubmit={handleCreateFamily}>
                  <div className="mb-3">
                    <label htmlFor="familyName" className="form-label">Family Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="familyName"
                      value={newFamilyName}
                      onChange={(e) => setNewFamilyName(e.target.value)}
                      placeholder="Enter family name"
                      required
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    Create Family
                  </button>
                </form>
              </div>
            </div>
          </div>
          
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center position-relative mb-3">
                  <h3 className="card-title mb-0">Join a Family</h3>
                  <button 
                    className="btn btn-link p-0 ms-2" 
                    onClick={() => setShowJoinTip(!showJoinTip)}
                    aria-label="Show join tips"
                    title="How to join a family"
                    ref={joinTipButtonRef}
                  >
                    <i className="bi bi-question-circle"></i>
                  </button>
                  
                  {/* Join Tip Popover */}
                  {showJoinTip && renderTipPopover(
                    <div className="text-start">
                      <p className="mb-2">To join an existing family:</p>
                      <ol className="mb-0">
                        <li>Ask the family owner for their join code</li>
                        <li>Enter the join code in the field below</li>
                        <li>Enter your name as you want it to appear</li>
                        <li>Click "Join Family"</li>
                      </ol>
                      <p className="mt-2 mb-0">Once joined, you'll be able to track your Bible reading progress with this family.</p>
                    </div>,
                    joinTipRef
                  )}
                </div>
                <form onSubmit={handleJoinFamily}>
                  <div className="mb-3">
                    <label htmlFor="joinCode" className="form-label">Join Code</label>
                    <input
                      type="text"
                      className="form-control"
                      id="joinCode"
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value)}
                      placeholder="Enter join code"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="readerName" className="form-label">Your Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="readerName"
                      value={readerName}
                      onChange={(e) => setReaderName(e.target.value)}
                      placeholder="Enter your name"
                      required
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    Join Family
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        
        {/* Readers Management */}
        {selectedFamily && (
          <div className="readers-section mb-4">
            <div className="d-flex align-items-center position-relative">
              <h2>Readers in {currentFamily?.name}</h2>
              <button 
                className="btn btn-link p-0 ms-2" 
                onClick={() => setShowReaderTip(!showReaderTip)}
                aria-label="Show reader tips"
                title="About readers"
                ref={readerTipButtonRef}
              >
                <i className="bi bi-question-circle"></i>
              </button>
              
              {/* Reader Tip Popover */}
              {showReaderTip && renderTipPopover(
                <div className="text-start">
                  <p>Add each person who will be tracking their Bible reading:</p>
                  <ul className="mb-0">
                    <li>Each family member should have their own reader profile</li>
                    <li>Reading progress is tracked individually for each reader</li>
                    <li>Children can have their own reader profiles to track their progress</li>
                    <li>You can add as many readers as needed to your family</li>
                  </ul>
                </div>,
                readerTipRef
              )}
            </div>
            
            {/* Add Reader Form */}
            <div className="card mb-3">
              <div className="card-body">
                <h3 className="card-title">Add a Reader</h3>
                <form onSubmit={handleAddReader} className="d-flex">
                  <input
                    type="text"
                    className="form-control me-2"
                    value={newReaderName}
                    onChange={(e) => setNewReaderName(e.target.value)}
                    placeholder="Reader name"
                    required
                  />
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    Add
                  </button>
                </form>
              </div>
            </div>
            
            {/* Readers List */}
            <div className="card">
              <div className="card-header">
                <h3 className="mb-0">Current Readers</h3>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : readers && readers.length > 0 ? (
                  <div className="readers-list">
                    <div className="list-group">
                      {readers.map(reader => (
                        <div key={reader.id} className="list-group-item d-flex justify-content-between align-items-center">
                          <div>
                            <h5 className="mb-1">{reader.name}</h5>
                            <small>ID: {reader.id}</small>
                          </div>
                          <div className="reader-actions">
                            <Link 
                              to={`/reader/${reader.id}`}
                              className="btn btn-outline-primary btn-sm me-2"
                            >
                              View Profile
                            </Link>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleRemoveReader(reader.id)}
                              disabled={loading}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p>No readers in this family yet. Add a reader above.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Family; 